import { ClearOutlined } from "@ant-design/icons"
import { App, Flex, Skeleton } from "antd"
import React, { useCallback, useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import { type ConnectedProps, connect } from "react-redux"
import { useNavigate } from "react-router"
import { useSearchParams } from "react-router-dom"

import { FixedContainer } from "../../../components/FixedContainer.tsx"
import { generateDocumentTitle } from "../../../constants/document-title.ts"
import { RoutesPaths } from "../../../constants/routes.ts"
import type { RootState } from "../../../store.ts"
import { downloadBlob } from "../../../utils/download-file.ts"
import { serializeData } from "../../../utils/utils"
import {
  updateProject,
  updateProjectStatus
} from "../../Project/redux/project.thunks.ts"
import { ProjectsEmpty } from "../components/ProjectsEmpty.tsx"
import {
  CREATED_DATE_FILTER_FIELD,
  DEFAULT_PROJECT_ORDERING
} from "../constants/created.ts"
import { projectsSetGridFilters } from "../redux/projects.slice.ts"
import {
  ProjectFetchType,
  deleteProject,
  fetchImportProjectsTemplates,
  fetchProjects,
  fetchProjectsFilterOptions,
  importProjects
} from "../redux/projects.thunks.ts"
import type { ProjectFilters } from "../redux/projects.types.ts"
import {
  ProjectListGrid,
  projectListGridClassName
} from "./components/ProjectListGrid.tsx"
import { ProjectListGridFilters } from "./components/ProjectListGridFilters.tsx"
import type { SortOption } from "./components/ProjectListGridFiltersOrdering.tsx"
import { ProjectListGridHeader } from "./components/ProjectListGridHeader.tsx"

const ITEMS_PER_PAGE = 12

function ProjectsGridPage({
  data,
  filters,
  fetching,
  fetchingFilterOptions,
  filterOptions,
  projectPermissions,
  updateFilters,
  updateProject,
  updateProjectStatus,
  fetchProjects,
  fetchFilterOptions,
  importProjects,
  getImportProjectsTemplates,
  deleteProject
}: ConnectedProps<typeof connector>) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const isLoadingMoreRef = useRef<boolean>(null)
  const { modal } = App.useApp()

  const isInitialLoading = !isLoadingMoreRef?.current && fetching
  const moduleAccess = projectPermissions?.projects
  const moduleFeatures = projectPermissions?.features

  const handleChangeFilter = useCallback(
    (newData: Partial<ProjectFilters>) => {
      isLoadingMoreRef.current = false
      updateFilters({ ...newData, page: 1 })
    },
    [updateFilters]
  )

  const handleGetProjects = useCallback(() => {
    const serializeFilters = serializeData(filters) as ProjectFilters
    fetchProjects({
      type: ProjectFetchType.GRID,
      filters: {
        ...serializeFilters,
        ordering: serializeFilters.ordering ?? DEFAULT_PROJECT_ORDERING,
        page_size: ITEMS_PER_PAGE
      }
    })
  }, [fetchProjects, filters])

  const handleLoadMoreData = useCallback(() => {
    if (!data?.next) return
    isLoadingMoreRef.current = true

    const urlObj = new URL(data?.next)
    const params = new URLSearchParams(urlObj.search)
    const paramsObj = Object.fromEntries(params)
    const serializeFilters = serializeData(paramsObj) as ProjectFilters

    fetchProjects({
      type: ProjectFetchType.GRID,
      isMore: true,
      filters: { ...serializeFilters, page_size: ITEMS_PER_PAGE }
    })
  }, [fetchProjects, filters, data?.next])

  const handleProjectClick = useCallback(
    (id: string) => {
      navigate(RoutesPaths.project.view(id).overview)
    },
    [navigate]
  )

  const handleStatusChange = useCallback(
    async (id: string, project_status: number) => {
      await updateProjectStatus({
        project: { id, project_status },
        mode: "grid"
      })
    },
    [updateProjectStatus]
  )

  const handleDeleteProject = useCallback(
    (id: string) => {
      modal.confirm({
        centered: true,
        title: "Confirm deleting",
        content:
          "Are you sure you want to delete this project? This action can NOT be undone.",
        cancelText: "Cancel",
        okText: "Delete",
        okButtonProps: { danger: true, ghost: true },
        onOk: async () => {
          try {
            await deleteProject(id).unwrap()
            handleGetProjects()
          } catch (e) {
            console.log(e)
          }
        }
      })
    },
    [modal, deleteProject, handleGetProjects]
  )

  const handleImportLeads = useCallback(
    async (file: File) => {
      try {
        await importProjects(file).unwrap()
        handleGetProjects()
      } catch (e) {
        console.log(e)
      }
    },
    [importProjects, handleGetProjects]
  )

  const handleGetImportLeadsTemplates = useCallback(async () => {
    const blob = await getImportProjectsTemplates()?.unwrap()
    downloadBlob(blob, `import-projects-template.xlsx`)
  }, [getImportProjectsTemplates])

  const handleUpdateMainPhoto = useCallback(
    async (projectId: string, file?: File | null): Promise<string | null> => {
      try {
        const res = await updateProject({
          id: projectId,
          main_photo: file
        }).unwrap()
        return res?.main_photo || null
      } catch (e) {
        console.log(e)
        return null
      }
    },
    [updateProject]
  )

  useEffect(() => {
    const params: Partial<ProjectFilters> = {}
    const status = searchParams.get("project_status")
    const q = searchParams.get("q")
    const manager = searchParams.get("project_manager")
    const ordering = searchParams.get("ordering")
    if (status) params.project_status = status
    if (q) params.q = q
    if (manager) params.project_manager = manager
    if (ordering) params.ordering = ordering
    const created: Partial<ProjectFilters> = {
      created_window: searchParams.get("created_window"),
      created_after: searchParams.get("created_after"),
      created_before: searchParams.get("created_before")
    }
    // A link names its whole creation range: one left from an earlier drill would narrow it.
    if (
      Object.keys(params).length > 0 ||
      Object.values(created).some(Boolean)
    ) {
      updateFilters({ ...params, ...created, page: 1 })
    }
  }, [])

  useEffect(() => {
    handleGetProjects()
  }, [handleGetProjects, filters])

  useEffect(() => {
    generateDocumentTitle("Projects")
    fetchFilterOptions()
  }, [fetchFilterOptions])

  return (
    <FixedContainer>
      <Flex vertical gap={"middle"} className="max-h-full">
        <ProjectListGridHeader
          projectPermissions={projectPermissions}
          search={filters?.q || ""}
          onImport={handleImportLeads}
          onFilersChange={handleChangeFilter}
          onRefetchProjectsCallback={handleGetProjects}
          onDownloadTemplate={handleGetImportLeadsTemplates}
        />

        <ProjectListGridFilters
          filters={filters}
          fetching={fetchingFilterOptions}
          filterOptions={filterOptions}
          sortOptions={[
            { key: "lead_name", label: t("filters.nameLabel") },
            { key: "customer_name", label: t("filters.customerLabel") },
            {
              key: "project_managers",
              label: t("filters.projectManagerLabel")
            },
            {
              key: "sales_price",
              label: t("filters.budgetLabel"),
              hidden: !projectPermissions?.features?.project_show_budget
            },
            { key: "updated", label: t("filters.updatedDateLabel") },
            { key: "created", label: "Created Date", firstOrder: "desc" },
            { key: "end_date", label: t("filters.dueDate") },
            {
              key: null,
              hidden: !filters?.ordering,
              label: t("filters.removeOrdering"),
              icon: <ClearOutlined />
            }
          ]}
          filterFields={[
            {
              key: "project_manager",
              label: "By manager(s)",
              placeholder: "Filter by manager(s)",
              type: "multiselect"
            },
            {
              key: "customer_name",
              label: t("filters.byCustomer"),
              placeholder: t("filters.customerPlaceholder"),
              type: "text"
            },
            {
              key: "end_date_after",
              endKey: "end_date_before",
              label: t("filters.byDueDate"),
              placeholder: t("filters.dueDatePlaceholder"),
              type: "daterange"
            },
            CREATED_DATE_FILTER_FIELD
          ]}
          onFiltersChange={handleChangeFilter}
        />

        {isInitialLoading ? (
          <div className="flex-1 overflow-auto tailwind-scroll">
            <div className={projectListGridClassName}>
              {Array.from({ length: 20 }).map((_, i) => (
                <Skeleton.Node
                  key={i}
                  className="w-full h-80"
                  active
                  style={{ width: "100%", height: "320px" }}
                />
              ))}
            </div>
          </div>
        ) : (
          <>
            {data?.results?.length ? (
              <ProjectListGrid
                projects={data?.results || []}
                hasMore={!!data?.next}
                statuses={filterOptions?.project_status || []}
                statusEditable={!!moduleAccess?.project_edit}
                photoEditable={!!moduleAccess?.project_edit}
                showProgress={!!moduleFeatures?.project_show_progress}
                showBudget={!!moduleFeatures?.project_show_budget}
                deleteAllowed={!!moduleAccess?.project_delete}
                onLoadMoreProjects={handleLoadMoreData}
                onProjectClick={handleProjectClick}
                onDeleteProject={handleDeleteProject}
                onStatusChange={handleStatusChange}
                onUpdateMainPhoto={handleUpdateMainPhoto}
              />
            ) : (
              <ProjectsEmpty hasSearch={!!filters?.q} />
            )}
          </>
        )}
      </Flex>
    </FixedContainer>
  )
}

const mapStateToProps = (state: RootState) => ({
  data: state.projects.gridData,
  filters: state.projects.gridFilters,
  fetching: state.projects.fetching,
  fetchingFilterOptions: state.projects.fetchingFilterOptions,
  filterOptions: state.projects.filterOptions,
  projectPermissions: state.auth.permissions?.codes?.projects
})

const mapDispatchToProps = {
  fetchProjects,
  updateProject,
  updateProjectStatus,
  fetchFilterOptions: fetchProjectsFilterOptions,
  updateFilters: projectsSetGridFilters,
  importProjects,
  getImportProjectsTemplates: fetchImportProjectsTemplates,
  deleteProject
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(ProjectsGridPage)
